import React, { useMemo } from 'react';
import { HighlightCard } from '../HighlightCard/HighlightCard';
import { Box, styled } from '@mui/material';
import { HighlightsProps } from './HighlightsSlider.props';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

export const HIGHLIGHT_COUNT = 3;

const StyledSwiper = styled(Swiper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '.swiper-wrapper': {
    display: 'flex',
    alignItems: 'center',
    '.swiper-slide': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  },
  '.swiper-pagination': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    width: '100%',
    padding: '8px 0',
    '.swiper-pagination-bullet': {
      width: '12px',
      height: '12px',
      backgroundColor: '#808888',
      borderRadius: '50%',
      cursor: 'pointer'
    },
    '.swiper-pagination-bullet-active': {
      backgroundColor: '#193051',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      cursor: 'default'
    }
  }
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  flexWrap: 'wrap'
}));

export const HighlightsSlider: React.FC<HighlightsProps> = ({
  activeTab,
  highlights,
  fallbackHighlights
}) => {
  const pages = useMemo(() => {
    const result = [];
    const highlightsWithPoints = highlights.filter((h) => Number(h.points) > 0);
    const existingRanks = new Set(highlightsWithPoints.map((h) => h.rank));
    const missingFallbacks = fallbackHighlights.filter((fb) => !existingRanks.has(fb.rank));
    const combinedHighlights = [...highlightsWithPoints, ...missingFallbacks].sort((a, b) => a.rank - b.rank);
    for (let i = 0; i < combinedHighlights.length; i += HIGHLIGHT_COUNT) {
      const page = combinedHighlights.slice(i, i + HIGHLIGHT_COUNT);
      result.push(page);
    }
    return result;
  }, [highlights, fallbackHighlights]);
  return (
    <StyledSwiper
      modules={[Pagination]}
      pagination={pages.length > 1 ? { clickable: true } : false}>
      {pages.map((page, pageIndex) => (
        <SwiperSlide key={`${activeTab}-${pageIndex}`}>
          <StyledBox>
            {Array.from({ length: HIGHLIGHT_COUNT }).map((_, index) => {
              const highlightItem = page[index];
              return (
                <HighlightCard
                  key={highlightItem?.id || `empty-${pageIndex}-${index}`}
                  sx={
                    !highlightItem
                      ? { ...(page.length === 1 ? { display: 'none' } : { visibility: 'hidden' }) }
                      : {}
                  }
                  {...highlightItem}
                />
              );
            })}
          </StyledBox>
        </SwiperSlide>
      ))}
    </StyledSwiper>
  );
};
